// ========================= Constants =========================
const DEBUGGING =
  window.Shopify?.theme?.role === "development" ||
  window.Shopify?.theme?.role === "unpublished";
const IS_SHOPIFY_EDITOR =
  window.Shopify &&
  (window.Shopify.designMode ||
    (window.Shopify.theme && window.Shopify.theme.editor));
const KLAVIYO_ENABLED = false;
const FREE_SHIPPING_THRESHOLD = 49.0;
const SHIPPING_COST = 3.9;

/* ------ Klaviyo Events ------ */
const KLAVIYO_EVENTS = {
  QUIZ_START_VIEWED: "Quiz Start Viewed",
  QUIZ_COMPLETED: "Quiz Completed",
  QUIZ_NEW_STARTED: "Quiz New Started",
  QUIZ_NEW_SKIPPED: "Quiz New Skipped",
  QUIZ_RESULTS_VIEWED: "Quiz Results Viewed",
  OFFER_VIEWED: "Offer Viewed",
  OFFER_MONTHLY_CLICKED: "Offer Monthly Clicked",
  OFFER_TRIAL_CLICKED: "Offer Trial Clicked",
  OFFER_ADDED_TO_CART: "Offer Added to Cart",
};

/* ------ Klaviyo API ------ */
const KLAVIYO_API = {
  fetchKlaviyoAjax: async (endpoint = "", options = {}) => {
    const defaultOptions = { headers: KLAVIYO_HEADERS, method: "GET" };
    const ajaxOptions = {
      ...defaultOptions,
      ...options,
      headers: KLAVIYO_HEADERS, // Ensure headers are not overridden
    };

    if (ajaxOptions.body && typeof ajaxOptions.body === "object") {
      ajaxOptions.data = JSON.stringify(ajaxOptions.body);
      delete ajaxOptions.body;
    }

    const url = `${KLAVIYO_API_BASE_URL}${endpoint}`;
    return new Promise((resolve, reject) => {
      $.ajax({
        url: url,
        crossDomain: true,
        type: ajaxOptions.method,
        headers: ajaxOptions.headers,
        data: ajaxOptions.data,
        contentType: "application/json",
        success: (response) => resolve(response),
        error: (error) => reject(error),
      });
    });
  },
  fetchKlaviyo: async (endpoint = "", options = {}) => {
    const defaultOptions = {
      headers: KLAVIYO_HEADERS,
      method: "GET",
    };
    const fetchOptions = {
      ...defaultOptions,
      ...options,
      headers: KLAVIYO_HEADERS, // Ensure headers are not overridden
    };

    if (fetchOptions.body && typeof fetchOptions.body === "object") {
      fetchOptions.body = JSON.stringify(fetchOptions.body);
    }

    const url = `${KLAVIYO_API_BASE_URL}${endpoint}`;
    log.collapse({ url, fetchOptions }, "Fetching Klaviyo Options");
    const response = await fetch(url, fetchOptions);
    return response.json();
  },
  getEvents: async () => {
    return KLAVIYO_API.fetchKlaviyo("/events");
  },
  createProfile: async (profileData) => {
    // Format the profile data according to Klaviyo API requirements
    const formattedProfile = {
      data: {
        type: "profile",
        attributes: {
          email: profileData.email,
          first_name: profileData.first_name || profileData.name,
          locale: "pt-PT", // Always set to pt-PT
          location: {
            region: "PT", // Always set to PT
          },
          properties: {
            $source: "Kandy Quiz", // Set source to always be Kandy Quiz
            "Accepts Marketing": true, // Set always true
            Account_to_claim: true, // Set always true
            ...profileData.properties,
          },
        },
      },
    };

    log.collapse(
      "Creating Klaviyo profile:",
      formattedProfile,
      "Creating Klaviyo profile"
    );

    return KLAVIYO_API.fetchKlaviyo("/profiles", {
      method: "POST",
      body: formattedProfile,
    });
  },
  getProfileByEmail: async (email) => {
    return KLAVIYO_API.fetchKlaviyo(
      `/profiles/?filter=equals(email,"${email}")`
    );
  },
  updateProfile: async (profileId, profileData) => {
    // Format the profile data according to Klaviyo API requirements
    const formattedProfile = {
      data: {
        id: profileId,
        type: "profile",
        attributes: {
          properties: profileData.properties || {},
        },
      },
    };

    // Add other attributes if provided
    if (profileData.email)
      formattedProfile.data.attributes.email = profileData.email;
    if (profileData.first_name)
      formattedProfile.data.attributes.first_name = profileData.first_name;
    if (profileData.phone_number)
      formattedProfile.data.attributes.phone_number = profileData.phone_number;

    log.collapse(
      "Updating Klaviyo profile:",
      formattedProfile,
      "Updating Klaviyo profile"
    );

    return KLAVIYO_API.fetchKlaviyo(`/profiles/${profileId}`, {
      method: "PATCH",
      body: formattedProfile,
    });
  },

  quizAnswersToProfileProperties: (answers) => {
    if (!answers) return {};

    return {
      dog1_name: answers.name || "",
      dog1_gender: answers.gender || "",
      dog1_lifestage: answers.age || "Adult (1 to 7 years)",
      dog1_breed: answers.breed || "unknown",
      dog1_weight: answers.weightCategory || "unknown",
      dog1_monthly_food_amount: `${answers.weight} kg` || "",
      dog1_flavour_preference: answers.preferredProtein || "",
      dog1_feeding_habits: answers.typeOfFood || "",
      dog1_improve: answers.concerns || "",
    };
  },

  createProfileFromQuizAnswers: async (answers, email) => {
    if (!answers || !email) {
      log.collapse(
        "Missing required data for Klaviyo profile creation: answers or email",
        "Missing Data for Klaviyo",
        "error"
      );
      return null;
    }

    const profileData = {
      email: email,
      first_name: answers.user_name || "",
      properties: KLAVIYO_API.quizAnswersToProfileProperties(answers),
    };

    try {
      const result = await KLAVIYO_API.createProfile(profileData);
      log.collapse(result, "Klaviyo profile created", success);
      return result;
    } catch (error) {
      log.collapse(
        `Error creating Klaviyo profile: ${error}`,
        "Error creating Klaviyo profile",
        "error"
      );
      return null;
    }
  },

  trackEvent: async (eventName, email, properties = {}) => {
    if (!eventName || !email) {
      log.collapse(
        "Missing required data for Klaviyo event tracking: eventName or email",
        "Missing Data for Klaviyo",
        "error"
      );
      return null;
    }

    const eventData = {
      data: {
        type: "event",
        attributes: {
          profile: {
            email: email,
          },
          metric: {
            name: eventName,
          },
          properties: properties,
        },
      },
    };

    log.collapse(eventData, `Tracking Klaviyo event: ${eventName}`);

    try {
      const result = await KLAVIYO_API.fetchKlaviyo("/events", {
        method: "POST",
        body: eventData,
      });

      log.collapse(result, `Klaviyo event ${eventName} tracked`, success);
      return result;
    } catch (error) {
      log.collapse(
        error,
        `Error tracking Klaviyo event: ${eventName}`,
        "error"
      );
      return null;
    }
  },

  trackQuizCompleted: async (answers) => {
    if (!answers || !answers.email) {
      log.collapse(
        "Missing email for tracking quiz completion",
        "Missing Data for Klaviyo",
        "error"
      );
      return null;
    }

    const properties = {
      quizVersion: answers.quizVersion || "2.0",
      timestamp: new Date().toISOString(),
      dogName: answers.name || "",
      dogBreed: answers.breed || "",
      dogAge: answers.age || "",
      dogWeight: answers.weight || "",
      dogGender: answers.gender || "",
      preferredProtein: answers.preferredProtein || "",
      isGrainFree: answers.isGrainFree || false,
      typeOfFood: answers.typeOfFood || "",
      concerns: answers.concerns || "",
    };

    return KLAVIYO_API.trackEvent(
      KLAVIYO_EVENTS.QUIZ_COMPLETED,
      answers.email,
      properties
    );
  },

  trackQuizStartViewed: async (email = null) => {
    if (!email) return null;

    const properties = {
      timestamp: new Date().toISOString(),
      source: window.location.href,
    };

    return KLAVIYO_API.trackEvent(
      KLAVIYO_EVENTS.QUIZ_START_VIEWED,
      email,
      properties
    );
  },

  trackOfferViewed: async (email, dogDetails = null, productVariant = null) => {
    if (!email) return null;

    const properties = {
      timestamp: new Date().toISOString(),
      source: window.location.href,
    };

    if (dogDetails) {
      properties.dogName = dogDetails.name || "";
      properties.dogBreed = dogDetails.breed || "";
      properties.dogAge = dogDetails.age || "";
      properties.dogWeight = dogDetails.weight || "";
    }

    if (productVariant) {
      properties.productTitle = productVariant.title || "";
      properties.productWeight = productVariant.weight || "";
      properties.productPrice = productVariant.price || 0;
    }

    return KLAVIYO_API.trackEvent(
      KLAVIYO_EVENTS.OFFER_VIEWED,
      email,
      properties
    );
  },

  trackOfferAddedToCart: async (email, productData, planType) => {
    if (!email || !productData) return null;

    const properties = {
      timestamp: new Date().toISOString(),
      source: window.location.href,
      planType: planType || "monthly",
      productTitle: productData.title || "",
      productWeight: productData.weight || "",
      productPrice: productData.price || 0,
      variantId: productData.variantId || "",
      isGrainFree: productData.isGrainFree || false,
    };

    return KLAVIYO_API.trackEvent(
      KLAVIYO_EVENTS.OFFER_ADDED_TO_CART,
      email,
      properties
    );
  },

  // Track offer plan clicked events (trial/monthly)
  trackOfferPlanClicked: async (email, planType, productData) => {
    if (!email || !planType) return null;

    const eventName =
      planType === "trial"
        ? KLAVIYO_EVENTS.OFFER_TRIAL_CLICKED
        : KLAVIYO_EVENTS.OFFER_MONTHLY_CLICKED;

    const properties = {
      timestamp: new Date().toISOString(),
      source: window.location.href,
    };

    if (productData) {
      properties.productTitle = productData.title || "";
      properties.productWeight = productData.weight || "";
      properties.productPrice = productData.price || 0;
    }

    return KLAVIYO_API.trackEvent(eventName, email, properties);
  },
};

/* ------ Klaviyo Profile Example Structure ------ */
const KLAVIYO_CREATE_PROFILE = {
  data: {
    type: "profile",
    attributes: {
      email: "benjaminjo@rose.net", // Quiz answer: email
      first_name: "Benny", // Quiz answer: user_name
      locale: "pt-PT", // Always set to pt-PT
      location: {
        region: "PT", // Always set to PT
      },
      properties: {
        $source: "Kandy Quiz", // Set source to always be Kandy Quiz
        "Accepts Marketing": true, // Set always true
        Account_to_claim: true, // Set always true
        dog1_name: "Rosa", // Quiz answer: name
        dog1_gender: "Macho", // Quiz answer: gender
        dog1_lifestage: "Adult (1 to 7 years)", // Quiz answer: age
        dog1_breed: "Basset Fauve de Bretagne", // Quiz answer: breed
        dog1_weight: 9, // Quiz answer: weight category
        dog1_monthly_food_amount: "18 kg", // Quiz answer: weight
        dog1_flavour_preference: "Peixe", // Quiz answer: preferred protein
        dog1_feeding_habits: "Apenas comida húmida", // Quiz answer: type of food
        dog1_improve: "Dysplasia and joint problems", // Quiz answer: concerns
      },
    },
  },
};
const KLAVIYO_UPDATE_PROFILE = {
  data: {
    id: "01JP7TSAP0M92CSSAT8YG6MXKD",
    type: "profile",
    attributes: {
      properties: {
        dog1_gender: "Fêmea",
      },
    },
  },
};

// ========================= Core Utilities =========================

/**
 * Validates email format and returns [isInvalid, message]
 * @param str - Email string to validate
 * @returns [boolean, string] - [true if invalid, validation message]
 */
function emailValidation(str) {
  const country = window.Shopify?.country || "PT";

  const messages = {
    PT: {
      empty: "O email não pode estar em branco",
      long: "O email é muito longo (máx 100 caracteres)",
      short: "O email deve ter pelo menos 10 caracteres",
      atSymbol: "O email deve conter um símbolo @",
      minLocale: "O nome do email deve ter pelo menos 3 caracteres",
      doubleDot: "O email não pode conter 2 pontos seguidos",
      startDot: "O email não pode começar com um ponto",
      noDotDomain: "Domínio incorreto (falta o ponto)",
      minDomainEnd: "O domínio deve terminar com pelo menos 2 letras",
    },
    ES: {
      empty: "El email no puede estar en blanco",
      long: "El email es demasiado largo (máx 100)",
      short: "El email debe tener al menos 10 caracteres",
      atSymbol: "El email debe contener un símbolo @",
      minLocale: "El nombre debe tener al menos 3 caracteres",
      doubleDot: "No puede contener 2 puntos seguidos",
      startDot: "No puede empezar con un punto",
      noDotDomain: "Dominio incorrecto (falta el punto)",
      minDomainEnd: "El dominio debe terminar con al menos 2 letras",
    },
    IT: {
      empty: "L'email non può essere vuota",
      long: "L'email è troppo lunga (max 100)",
      short: "L'email deve avere almeno 10 caratteri",
      atSymbol: "L'email deve contenere @",
      minLocale: "Il nome deve avere almeno 3 caratteri",
      doubleDot: "Non può contenere 2 punti consecutivi",
      startDot: "Non può iniziare con un punto",
      noDotDomain: "Dominio errato (manca il punto)",
      minDomainEnd: "Il dominio deve terminare con almeno 2 lettere",
    },
  };

  const t = messages[country] || messages["PT"];

  if (!str || str.trim() === "") return [true, t.empty];

  str = str.trim().replace(/\s+/g, " ");

  if (str.length > 100) return [true, t.long];
  if (str.length < 10) return [true, t.short];

  const parts = str.split("@");
  if (parts.length !== 2) return [true, t.atSymbol];

  const [locale, domain] = parts;

  if (locale.length < 3) return [true, t.minLocale];
  if (str.includes("..")) return [true, t.doubleDot];

  if (locale[0] === ".") return [true, t.startDot];

  const allowedLocaleSymbols =
    /^([a-zA-Z0-9\.\+\-\_\!\#\$\%\&\'\*\/\=\?\^\`\{\|\}\~])/;
  for (const s of locale) {
    if (!allowedLocaleSymbols.test(s)) return [true, `Símbolo restrito: ${s}`];
  }

  const domainParts = domain.split(".");
  if (domainParts.length === 1) return [true, t.noDotDomain];
  if (domainParts[domainParts.length - 1].length < 2)
    return [true, t.minDomainEnd];

  return [false, "OK"];
}

function uuid() {
  return (
    Math.random().toString(36).substring(2, 12) +
    Math.random().toString(36).substring(2, 12)
  );
}

const QuizStorage = {
  KEYS: {
    QUIZ_ANSWERS: "kandyQuizAnswers",
    DOG_DETAILS: "kandyDogDetails",
    PRODUCT_VARIANT: "kandyProductVariant",
    TRIAL_VARIANT: "kandyTrialVariant",
  },

  setQuizAnswers(answers, isCompleted = false) {
    const answersWithCompletionStatus = {
      ...answers,
      isCompleted: isCompleted,
    };
    LocalStorageManager.setWithExpiry(
      this.KEYS.QUIZ_ANSWERS,
      answersWithCompletionStatus,
      14
    );
  },

  getQuizAnswers() {
    return LocalStorageManager.getWithExpiry(this.KEYS.QUIZ_ANSWERS);
  },

  setDogDetails(answers) {
    // Calculate trial weight based on the dog's weight
    let trialWeight = "3kg";
    if (answers.weight) {
      const weightNum = parseFloat(
        answers.weight.toString().replace(/[^\d.]/g, "")
      );
      trialWeight = weightNum <= 6 ? "1kg" : "3kg";
    }

    const dogDetails = {
      name: answers.name || "",
      age: answers.age || "Adult",
      weight: answers.weight || "2",
      trialWeight: trialWeight, // Add trial weight
      breed: answers.breed || "",
      gender: answers.gender || "",
      concerns: answers.concerns || "none",
      isGrainFree: answers.isGrainFree || false,
      preferredProtein: answers.preferredProtein || "Chicken",
      weightCategory: answers.weightCategory || "",
      weightCategoryText: answers.weightCategoryText || "",
      typeOfFood: answers.typeOfFood || "",
      timestamp: new Date().toISOString(),
    };
    // Store with 14-day expiry (2 weeks)
    LocalStorageManager.setWithExpiry(this.KEYS.DOG_DETAILS, dogDetails, 14);
  },

  getDogDetails() {
    return LocalStorageManager.getWithExpiry(this.KEYS.DOG_DETAILS);
  },

  setProductVariant(variant, isGrainFree) {
    const variantDetails = {
      title: variant.title || "",
      weight: variant.weight || "",
      price: variant.price || 0,
      compareAtPrice: variant.compareAtPrice || variant.price,
      productId: variant.productId || "",
      variantId: variant.variantId || "",
      sellingPlanId: variant.sellingPlanId || "",
      ...(isGrainFree && {
        options: {
          age: variant.ageCategory || "",
          protein: variant.protein || "",
        },
      }),
    };
    // Store with 14-day expiry (2 weeks)
    LocalStorageManager.setWithExpiry(
      this.KEYS.PRODUCT_VARIANT,
      variantDetails,
      14
    );
  },

  getProductVariant() {
    return LocalStorageManager.getWithExpiry(this.KEYS.PRODUCT_VARIANT);
  },

  setTrialVariant(variant, isGrainFree) {
    const variantDetails = {
      title: variant.title || "",
      weight: variant.weight || "",
      price: variant.price || 0,
      compareAtPrice: variant.compareAtPrice || variant.price,
      productId: variant.productId || "",
      variantId: variant.variantId || "",
      sellingPlanId: variant.sellingPlanId || "",
      ...(isGrainFree && {
        options: {
          age: variant.ageCategory || "",
          protein: variant.protein || "",
        },
      }),
    };
    // Store with 14-day expiry (2 weeks)
    LocalStorageManager.setWithExpiry(
      this.KEYS.TRIAL_VARIANT,
      variantDetails,
      14
    );
  },

  getTrialVariant() {
    return LocalStorageManager.getWithExpiry(this.KEYS.TRIAL_VARIANT);
  },

  clearExpiredData() {
    // Check if any of the key data has expired
    const quizAnswers = this.getQuizAnswers();
    const dogDetails = this.getDogDetails();
    const variant = this.getProductVariant();
    const trialVariant = this.getTrialVariant();

    // Note: We're not requiring the trial variant to be present
    // for all cases since not all quiz flows may set it

    // Log expiry status for debugging
    log.collapse(
      {
        quizAnswers: !!quizAnswers,
        dogDetails: !!dogDetails,
        variant: !!variant,
        trialVariant: !!trialVariant,
      },
      "Storage expiry check",
      "info"
    );

    // Only clear all data if core quiz data is missing
    if (!quizAnswers || !dogDetails) {
      // || !variant
      log.collapse(
        "Core quiz data is missing, clearing all related data",
        "Clearing Storage",
        "info"
      );
      localStorage.removeItem(this.KEYS.QUIZ_ANSWERS);
      localStorage.removeItem(this.KEYS.DOG_DETAILS);
      localStorage.removeItem(this.KEYS.PRODUCT_VARIANT);
      localStorage.removeItem(this.KEYS.TRIAL_VARIANT);
    }
  },

  debugStorage() {
    log.collapse(
      {
        quizAnswers: this.getQuizAnswers(),
        dogDetails: this.getDogDetails(),
        variant: this.getProductVariant(),
        trialVariant: this.getTrialVariant(),
      },
      "Quiz Storage Debug",
      "info"
    );
  },
};

const QuizValidation = {
  RULES: {
    weight: {
      validator: (value) => {
        const required = true;
        const minWeight = 1;
        const maxWeight = 99;
        if (required && (value === undefined || value === ""))
          return [true, "Introduz um número"];
        if (isNaN(value)) return [true, "Introduz um número válido"];
        if (value < minWeight)
          return [true, `Introduz um número entre ${minWeight} e ${maxWeight}`];
        if (value > maxWeight)
          return [true, `Introduz um número entre ${minWeight} e ${maxWeight}`];
        return [false, "OK"];
      },
    },
    name: {
      validator: (value) => {
        const required = true;
        const minLength = 2;
        const maxLength = 24;
        if (required && (!value || value.length === 0))
          return [true, "Introduz um nome"];
        if (value.length < minLength) {
          return [
            true,
            `Introduz um nome entre ${minLength} e ${maxLength} caracteres`,
          ];
        } else if (value.length > maxLength) {
          return [
            true,
            `Introduz um nome entre ${minLength} e ${maxLength} caracteres`,
          ];
        }
        return [false, "OK"];
      },
    },
    user_name: {
      validator: (value) => {
        const required = true;
        const minLength = 3;
        const maxLength = 24;
        if (required && (!value || value.length === 0))
          return [true, "Introduz um nome"];
        if (value.length < minLength) {
          return [
            true,
            `Introduz um nome entre ${minLength} e ${maxLength} caracteres`,
          ];
        } else if (value.length > maxLength) {
          return [
            true,
            `Introduz um nome entre ${minLength} e ${maxLength} caracteres`,
          ];
        }
        return [false, "OK"];
      },
    },
    email: {
      validator: emailValidation,
    },
    text: {
      validator: (value) => {
        const required = true;
        const minLength = 1;
        if (required && (!value || value.length === 0))
          return [true, "Introduz um texto"];
        if (value.length < minLength) {
          return [true, "Introduz um texto"];
        }
        return [false, "OK"];
      },
    },
  },

  /**
   * Validates an input based on its type and the defined rules
   * @param {jQuery} $input - The jQuery input element
   * @returns {boolean} - Whether the input is valid
   */
  validateInput($input) {
    const value = $input.val();
    let ruleType = "text"; // Default rule type

    const inputClass = $input.attr("class");
    if (inputClass.includes("qz__dogs_weight_input")) {
      ruleType = "weight";
    } else if (inputClass.includes("qz__name_input")) {
      ruleType = "name";
    } else if (inputClass.includes("qz__user_name_input")) {
      ruleType = "user_name";
    } else if (inputClass.includes("qz__email_input")) {
      ruleType = "email";
    }

    const rules = this.RULES[ruleType];

    const $inputErrorNotification = $input
      .closest(".Quiz_question_inner")
      .find(".Input_error_notification");
    const setNotification = (text) =>
      $inputErrorNotification.length &&
      $inputErrorNotification.show().find("p").text(text);
    const clearNotification = () =>
      $inputErrorNotification.length &&
      $inputErrorNotification.hide().find("p").text("");

    // Type-specific validations
    const valueToValidate = ruleType === "weight" ? parseFloat(value) : value;
    const [isInvalid, errorMessage] = rules.validator(valueToValidate);
    if (isInvalid) {
      setNotification(errorMessage);
      return false;
    } else {
      clearNotification();
    }

    return true;
  },

  /**
   * Shows validation error on an input
   * @param {jQuery} $input - The jQuery input element
   */
  showError($input) {
    $input.addClass("needsAction");
    setTimeout(function () {
      $input.removeClass("needsAction");
    }, 500);
  },
};

const Utils = {
  getRandomArbitrary: (min, max) =>
    Math.round(Math.random() * (max - min) + min),

  scrollToSection(selector, offset = 0) {
    $("html, body").animate(
      {
        scrollTop: $(selector).offset().top + offset,
      },
      500
    );
  },

  updateProgressBar(currentIndex, totalIndexes) {
    // Handle display of current question index
    const displayIndex = currentIndex === 1 ? 1 : currentIndex;
    $(".Quiz_header_question_info_js").html(
      displayIndex + " of " + totalIndexes
    );

    // Update progress bar
    const percentage = (currentIndex * 100) / totalIndexes;
    $(".Quiz-header_progres_bar__inner").css("width", percentage + "%");
  },

  generateRandomStats() {
    // Generate random statistics for results page
    return {
      similarResult: Utils.getRandomArbitrary(75, 87),
      statData1: Utils.getRandomArbitrary(76, 86),
      statData2: Utils.getRandomArbitrary(65, 76),
      statData3: Utils.getRandomArbitrary(41, 58),
      yourScore: Utils.getRandomArbitrary(25, 34),
    };
  },

  formatArrayToObject(array) {
    return array.reduce(
      (obj, item) => ({ ...obj, [item.key]: item.value }),
      {}
    );
  },
};

// ========================= Quiz Main Module =========================
const QuizMain = {
  init() {
    log = window.log;
    QuizStorage.clearExpiredData();

    const quizAnswers = QuizStorage.getQuizAnswers();
    // if (quizAnswers && !quizAnswers.isCompleted) {
    //   // If there's an incomplete quiz, clear it
    //   localStorage.removeItem(QuizStorage.KEYS.QUIZ_ANSWERS);
    //   localStorage.removeItem(QuizStorage.KEYS.DOG_DETAILS);
    //   localStorage.removeItem(QuizStorage.KEYS.PRODUCT_VARIANT);
    //   log('Cleared incomplete quiz data');
    // }

    // Prefill quiz answers if we have stored data
    this.prefillQuizAnswers(quizAnswers);

    // Debug storage
    QuizStorage.debugStorage();

    this.initializeQuizNavigation();
    this.bindEvents();
  },

  bindEvents() {
    this.bindNextQuestionTriggers();
    this.bindMultipleQuestionTriggers();
    this.bindTextQuestionTriggers();
    this.bindBreedSelection();
    this.bindWeightMeasurement();
  },

  initializeQuizNavigation() {
    $(".Quiz_header_back").click(function () {
      const current_index = parseFloat($(this).attr("data-current"));
      if (current_index > 1) {
        const next_index = current_index - 1;
        $(this).attr("data-current", next_index);
        $(".Quiz_question").hide();
        $(`.Quiz_question_${next_index}__js`).css("display", "flex");
        QuizMain.calculateTop();
      }
    });
  },

  calculateTop() {
    const currentIndex = parseFloat(
      $(".Quiz_header_back").attr("data-current")
    );
    const totalIndexes = parseFloat($(".Question_amount").attr("value"));
    Utils.updateProgressBar(currentIndex, totalIndexes);
  },

  moveToNextQuestion(thisEl) {
    const current_index = parseFloat(thisEl.attr("data-index"));

    $(".Answer_block").removeClass("active");
    thisEl.addClass("active");

    // Wait a moment before moving to the next question
    setTimeout(() => {
      QuizNavigation.moveToNextQuestion(current_index);
    }, 200);
  },

  getAnswers() {
    const existingAnswers = QuizStorage.getQuizAnswers() || {};
    // Preserve isCompleted flag if it exists
    const isCompleted = existingAnswers.isCompleted || false;

    // Get weight from either the weight container selection or the weight input
    const weightValue =
      $(".qz__dogs_weight_input").val() || existingAnswers.weight || "3";

    let foodBagWeight =
      $(".qz__weight_container .Answer_block.active").attr("data-value") ||
      existingAnswers.foodBagWeight ||
      "";
    // Remove 'kg' suffix and any spaces if present
    foodBagWeight = foodBagWeight
      .toString()
      .trim()
      .replace(/\s+/g, "")
      .replace(/kg/i, "")
      .replace("-", "");

    // Get weight category if available
    const weightCategory =
      $(".qz__weight_category_container .Answer_block.active").attr(
        "data-key"
      ) ||
      existingAnswers.weightCategory ||
      "";
    const weightCategoryText =
      $(".qz__weight_category_container .Answer_block.active .Answer").text() ||
      existingAnswers.weightCategoryText ||
      "";

    // Get breed if available
    const breed =
      $(".qz_breed_selection_input").val() || existingAnswers.breed || "";

    // Get age if available
    const age =
      $(".qz__age_container .Answer_block.active").attr("data-key") ||
      $(".qz__age_container .Answer_block.active").attr("data-value") ||
      $(".qz__age_container .Answer_block.active .Answer").text() ||
      existingAnswers.age ||
      "Adult";

    // Get gender if available
    const gender =
      $(".qz__gender_container .Answer_block.active").attr("data-key") ||
      $(".qz__gender_container .Answer_block.active").attr("data-value") ||
      existingAnswers.gender ||
      "";

    // Get protein preference if available
    const preferredProtein =
      $(".qz__protein_container .Answer_block.active").attr("data-key") ||
      $(".qz__protein_container .Answer_block.active").attr("data-protein") ||
      $(".qz__protein_container .Answer_block.active").attr("data-value") ||
      existingAnswers.preferredProtein ||
      "chicken";

    // Get grain-free preference if available
    const isGrainFree =
      $(".qz__grain_free_container .Answer_block.active").attr("data-key") ===
        "true" ||
      $(".qz__grain_free_container .Answer_block.active").attr(
        "data-grain-free"
      ) === "sim" ||
      $(".qz__grain_free_container .Answer_block.active").attr("data-value") ===
        "sim" ||
      existingAnswers.isGrainFree ||
      false;

    // Get type of food if available
    const typeOfFood =
      $(".qz__type_of_food_container .Answer_block.active").attr("data-key") ||
      $(".qz__type_of_food_container .Answer_block.active").attr(
        "data-food-type"
      ) ||
      $(".qz__type_of_food_container .Answer_block.active").attr(
        "data-value"
      ) ||
      existingAnswers.typeOfFood ||
      "";

    // Get concerns if available - check both multiple and single question types
    let concerns = existingAnswers.concerns || "";

    // Check for multiple selection concerns
    const multipleSelectionConcerns = $(
      ".qz__concerns_container .Multiple_question_trigger__js.active"
    )
      .map(function () {
        return (
          $(this).attr("data-key") ||
          $(this).attr("data-concern") ||
          $(this).attr("data-value")
        );
      })
      .get()
      .join(";");

    // Check for single selection concerns
    const singleSelectionConcern =
      $(".qz__concerns_container .Answer_block.active").attr("data-key") ||
      $(".qz__concerns_container .Answer_block.active").attr("data-concern") ||
      $(".qz__concerns_container .Answer_block.active").attr("data-value");

    // Prioritize new selections over existing values
    if (multipleSelectionConcerns) {
      concerns = multipleSelectionConcerns;
    } else if (singleSelectionConcern) {
      concerns = singleSelectionConcern;
    }

    // Get user email if available
    const userEmail =
      $(".qz__email_input").val() || existingAnswers.email || "";
    let validatedEmail = userEmail;
    if (userEmail) {
      const [isInvalid, _] = emailValidation(userEmail);
      if (isInvalid) {
        validatedEmail = existingAnswers.email || ""; // Keep existing email if new one is invalid
      }
    }

    // Get user name if available
    const userName =
      $(".qz__user_name_input").val() || existingAnswers.user_name || "";

    // Prepare the answers object, preserving existing data and updating with new values
    const answers = {
      ...existingAnswers,

      // Basic information
      name: $(".qz__name_input").val() || existingAnswers.name || "",
      weight: weightValue,
      breed: breed,
      foodBagWeight: foodBagWeight, // Store food bag weight as just a number

      // User information
      email: validatedEmail,
      user_name: userName,

      // Weight category information
      weightCategory: weightCategory,
      weightCategoryText: weightCategoryText,

      // Single choice answers
      age: age,
      gender: gender,
      preferredProtein: preferredProtein,
      isGrainFree: isGrainFree,
      typeOfFood: typeOfFood,

      // Multiple choice answers (health concerns)
      concerns: concerns,

      // Additional metadata
      timestamp: new Date().toISOString(),
      quizVersion: "2.0",

      // Preserve completion status
      isCompleted: isCompleted,
    };

    log.collapse(answers, "Storing quiz answers");

    QuizStorage.setQuizAnswers(answers, isCompleted);
    QuizStorage.debugStorage();

    // Update Question_amount data attributes for backward compatibility
    const questionAmount = $(".Question_amount");
    if (questionAmount.length) {
      questionAmount.attr("data-name", answers.name);
      questionAmount.attr("data-breed", answers.breed);
      questionAmount.attr("data-weight", answers.weight);
      questionAmount.attr("data-old", answers.age);
      questionAmount.attr("data-concerns", answers.concerns);
      questionAmount.attr("data-weight-category", answers.weightCategory);
      questionAmount.attr("data-type-of-food", answers.typeOfFood);
      questionAmount.attr("data-gender", answers.gender);
      questionAmount.attr("data-protein", answers.preferredProtein);
      questionAmount.attr(
        "data-grain-free",
        answers.isGrainFree ? "yes" : "no"
      );
      questionAmount.attr("data-email", answers.email);
      questionAmount.attr("data-user-name", answers.user_name);
    }

    return answers;
  },

  bindNextQuestionTriggers() {
    $(".Next_question_trigger__js").click(function () {
      const currentIndex = parseFloat($(this).attr("data-index"));
      const currentQuestionIndex = parseFloat(
        $(this).attr("data-question-index")
      );

      // Handle weight selection
      if (
        $(this)
          .closest(".Answer_container_gap_top")
          .hasClass("qz__weight_container")
      ) {
        // Get weight value and remove 'kg' suffix if present
        let weightValue = $(this).find("span.Answer").text();
        weightValue = weightValue
          .toString()
          .trim()
          .replace(/\s+/g, "")
          .replace(/kg/i, "");
        $(".Question_amount").attr("data-weight", weightValue);
      }

      // Handle weight category selection
      if (
        $(this)
          .closest(".Answer_container_gap_top")
          .hasClass("qz__weight_category_container")
      ) {
        const weightCategory =
          $(this).attr("data-key") || $(this).attr("data-value");
        $(".Question_amount").attr("data-weight-category", weightCategory);
      }

      // Handle age selection
      if (
        $(this)
          .closest(".Answer_container_gap_top")
          .hasClass("qz__age_container")
      ) {
        const age =
          $(this).find("span.Answer").text() || $(this).attr("data-value");
        $(".Question_amount").attr("data-old", age);
      }

      // Handle gender selection
      if (
        $(this)
          .closest(".Answer_container_gap_top")
          .hasClass("qz__gender_container")
      ) {
        const gender = $(this).attr("data-key") || $(this).attr("data-value");
        $(".Question_amount").attr("data-gender", gender);
      }

      // Handle protein preference selection
      if (
        $(this)
          .closest(".Answer_container_gap_top")
          .hasClass("qz__protein_container")
      ) {
        const protein =
          $(this).attr("data-key") ||
          $(this).attr("data-protein") ||
          $(this).attr("data-value");
        $(".Question_amount").attr("data-protein", protein);
      }

      // Handle grain-free selection
      if (
        $(this)
          .closest(".Answer_container_gap_top")
          .hasClass("qz__grain_free_container")
      ) {
        const grainFree =
          $(this).attr("data-key") ||
          $(this).attr("data-grain-free") ||
          $(this).attr("data-value");
        $(".Question_amount").attr("data-grain-free", grainFree);
      }

      // Handle type of food selection
      if (
        $(this)
          .closest(".Answer_container_gap_top")
          .hasClass("qz__type_of_food_container")
      ) {
        const typeOfFood =
          $(this).attr("data-key") ||
          $(this).attr("data-food-type") ||
          $(this).attr("data-value");
        $(".Question_amount").attr("data-type-of-food", typeOfFood);
      }

      // Handle concerns selection
      if (
        $(this)
          .closest(".Answer_container_gap_top")
          .hasClass("qz__concerns_container")
      ) {
        // For single question type, prioritize data-key over data-concern
        const concern =
          $(this).attr("data-key") ||
          $(this).attr("data-concern") ||
          $(this).attr("data-value");

        // Store the concern value
        if (concern) {
          $(".Question_amount").attr("data-concerns", concern);
        }
      }

      $(".Answer_block").removeClass("active");
      $(this).addClass("active");

      // Save answers after each question
      QuizMain.getAnswers();

      setTimeout(function () {
        QuizNavigation.moveToNextQuestion(currentIndex);
      }, 200);
    });
  },

  bindMultipleQuestionTriggers() {
    $(".Multiple_question_trigger__js").click(function () {
      $(this).toggleClass("active");
      let selected_selector = 0;

      $(".Multiple_question_trigger__js").each(function () {
        if ($(this).hasClass("active")) {
          selected_selector++;
        }
      });

      // Enable/disable next button based on selection
      if (selected_selector != 0) {
        $(".Multiple_question_next__js").removeClass("disabled");
      } else {
        $(".Multiple_question_next__js").addClass("disabled");
      }

      // Handle straight to next functionality
      if ($(this).hasClass("Multiple_straight_to_next__js")) {
        $(this)
          .closest(".Quiz_question_inner")
          .find(".Multiple_question_next__js")
          .trigger("click");
      }
    });

    $(".Multiple_question_next__js").click(function () {
      if ($(this).hasClass("disabled")) return;

      const currentIndex = parseFloat($(this).attr("data-index"));

      // Handle concerns if in concerns container
      if (
        $(this).closest(".Quiz_question_inner").find(".qz__concerns_container")
          .length
      ) {
        // Get all selected concerns
        const selectedConcerns = [];
        $(this)
          .closest(".Quiz_question_inner")
          .find(".qz__concerns_container .Multiple_question_trigger__js.active")
          .each(function () {
            const concernKey =
              $(this).attr("data-key") ||
              $(this).attr("data-concern") ||
              $(this).attr("data-value");
            if (concernKey) {
              selectedConcerns.push(concernKey);
            }
          });

        // Store concerns in Question_amount data attribute
        $(".Question_amount").attr("data-concerns", selectedConcerns.join(";"));
      }

      // Reset multiple selection state
      $(".Multiple_question_next__js").addClass("disabled");
      $(".Multiple_question_trigger__js").removeClass("active");

      // Save answers
      QuizMain.getAnswers();
      QuizNavigation.moveToNextQuestion(currentIndex);
    });
  },

  bindTextQuestionTriggers() {
    $(".Text_question_next__js").click(function () {
      const currentIndex = parseFloat($(this).attr("data-index"));

      // Save answers before moving to next question
      QuizMain.getAnswers();
      QuizNavigation.moveToNextQuestion(currentIndex);
    });

    $(".Text_question_next__js__input").click(function () {
      const $quizQuestionInner = $(this).closest(".Quiz_question_inner");
      const $textInput = $quizQuestionInner.find(".qz_text_input");
      const typedName = $quizQuestionInner
        .find(".qz_text_input.qz__name_input")
        .val();
      const typedUserName = $quizQuestionInner
        .find(".qz_text_input.qz__user_name_input")
        .val();
      const typedDogsWeight = $quizQuestionInner
        .find(".qz_text_input.qz__dogs_weight_input")
        .val();

      // Use the QuizValidation utility to validate the input
      if (!QuizValidation.validateInput($textInput)) {
        QuizValidation.showError($textInput);
        return;
      }

      // Get stored answers as fallback
      const storedAnswers = QuizStorage.getQuizAnswers() || {};
      const storedName = storedAnswers.name || "";
      const storedUserName = storedAnswers.user_name || "";

      // Replace name placeholders in questions and answers
      if (!typedDogsWeight) {
        $(".Quiz_medium_title").each(function () {
          let currentTitle = $(this).data("base-text") || $(this).html();

          // Use typed value if available, otherwise fallback to stored value
          const nameToUse = typedName || storedName || "o seu cão";
          const userNameToUse = typedUserName || storedUserName || "User";

          currentTitle = currentTitle
            .replace("(name)", nameToUse)
            .replace("[name]", nameToUse);
          currentTitle = currentTitle
            .replace("(user_name)", userNameToUse)
            .replace("[user_name]", userNameToUse);

          $(this).html(currentTitle);
        });
      }

      const $questionAmount = $(".Question_amount");
      $textInput.hasClass("qz__dogs_weight_input") &&
        $questionAmount.attr("data-weight", typedDogsWeight);
      $textInput.hasClass("qz__name_input") &&
        $questionAmount.attr("data-name", typedName);
      $textInput.hasClass("qz__user_name_input") &&
        $questionAmount.attr("data-user-name", typedUserName);

      // Save answers before moving to next question
      QuizMain.getAnswers();

      const currentIndex = parseFloat($(this).attr("data-index"));
      QuizNavigation.moveToNextQuestion(currentIndex);
    });

    // Bind email question next button
    $(".Email_question_next__js__input").click(function () {
      const $emailInput = $(this)
        .closest(".Quiz_question_inner")
        .find(".qz__email_input");
      const email = $emailInput.val();

      // Use the QuizValidation utility to validate the input
      if (!QuizValidation.validateInput($emailInput)) {
        QuizValidation.showError($emailInput);
        return;
      }

      // Store email in Question_amount data attribute
      $(".Question_amount").attr("data-email", email);

      // Save answers before moving to next question
      QuizMain.getAnswers();

      const currentIndex = parseFloat($(this).attr("data-index"));
      QuizNavigation.moveToNextQuestion(currentIndex);
    });
  },

  bindBreedSelection() {
    // Breed selection input handling
    $(".qz_breed_selection_input").keyup(function () {
      const value = $(this).val().toLowerCase();

      $(".qz_breed_inner_box_label").hide();
      $(".qz_breed_inner_box_label").each(function () {
        const thisVal = $(this).find("span").html().toLowerCase();
        if (thisVal.indexOf(value) !== -1) {
          $(this).show();
        }
      });
    });

    $(".qz_breed_selection_input").click(function () {
      $(".qz_breed_selector_inner_box").toggle();
    });

    $(".qz_breed_inner_box_label").click(function () {
      const currentValue = $(this).find("span").html();
      $(".qz_breed_selection_input").val(currentValue);
      $(".qz_breed_selector_inner_box").hide();

      // Update Question_amount data attribute
      $(".Question_amount").attr("data-breed", currentValue);

      QuizMain.getAnswers();

      const thisEl = $(this).closest(".qz_breed_selection_answering");
      QuizMain.moveToNextQuestion(thisEl);
    });

    $(".qz_not_sure__label").click(function () {
      // Set a default value for breed
      $(".qz_breed_selection_input").val("Mixed Breed");
      $(".Question_amount").attr("data-breed", "Mixed Breed");

      QuizMain.getAnswers();

      const thisEl = $(this).closest(".qz_breed_selection_answering");
      QuizMain.moveToNextQuestion(thisEl);
    });
  },

  bindWeightMeasurement() {
    $(".qz_weight_mea__curr").click(function () {
      $(this)
        .closest(".qz_cat_weight_measurement_cont")
        .find(".qz_weight_mea__curr")
        .removeClass("active");
      $(this).addClass("active");
    });
  },

  prefillQuizAnswers(answers) {
    if (!answers) return;

    log.collapse(answers, "Prefilling quiz answers");

    // Prefill text inputs
    if (answers.name) $(".qz__name_input").val(answers.name);
    if (answers.email) $(".qz__email_input").val(answers.email);
    if (answers.user_name) $(".qz__user_name_input").val(answers.user_name);

    // Prefill multiple choice answers (concerns)
    if (answers.concerns) {
      const concernsList = answers.concerns.split(";");
      $(".qz__concerns_container .Multiple_question_trigger__js").each(
        function () {
          const value =
            $(this).attr("data-key") ||
            $(this).attr("data-concern") ||
            $(this).attr("data-value");
          if (concernsList.includes(value)) {
            $(this).addClass("active");

            // Enable the next button if at least one concern is selected
            const nextButton = $(this)
              .closest(".Quiz_question_inner")
              .find(".Multiple_question_next__js");
            if (nextButton.length) {
              nextButton.removeClass("disabled");
            }
          }
        }
      );
    }
  },
};

// ========================= Quiz Results Module =========================
const QuizResults = {
  init() {
    log = window.log;
    const quizAnswers = QuizStorage.getQuizAnswers();

    // Redirect to quiz start if no answers or incomplete quiz
    if (
      !DEBUGGING &&
      !IS_SHOPIFY_EDITOR &&
      (!quizAnswers || !quizAnswers.isCompleted)
    ) {
      log.collapse(
        "No completed quiz answers found",
        "Quiz Incomplete",
        "error"
      );
      window.location.href = "/";
      return;
    }

    this.generateRandomStats();
    if (!quizAnswers) return;
  },

  generateRandomStats() {
    const stats = Utils.generateRandomStats();
    $(".summary_number_1__js").text(stats.statData1 + "%");
    $(".summary_number_2__js").text(stats.statData2 + "%");
    $(".summary_number_3__js").text(stats.statData3 + "%");
  },

  async createKlaviyoProfile(answers, email, reqSequence = 1) {
    log.collapse(
      {
        answers,
        email,
        reqSequence,
      },
      "createKlaviyoProfile",
      "info"
    );
    if (!answers || !email) return;

    if (typeof window.createKlaviyoProfile === "function") {
      const attributes = {
        email: email,
        first_name: answers.user_name || "",
        locale: "pt-PT",
        location: {
          region: "PT",
        },
        properties: {
          $source: "Kandy Quiz",
          "Accepts Marketing": true,
          Account_to_claim: true,
          dog1_name: answers.name || "",
          dog1_gender: answers.gender || "",
          dog1_lifestage: answers.age || "Adult (1 to 7 years)",
          dog1_breed: answers.breed || "unknown",
          dog1_weight: answers.weightCategory || "",
          dog1_monthly_food_amount: `${answers.weight} kg` || "",
          dog1_flavour_preference: answers.preferredProtein || "",
          dog1_feeding_habits: answers.typeOfFood || "",
          dog1_improve: answers.concerns || "",
        },
      };

      // Update Klaviyo profile
      window
        .createKlaviyoProfile(attributes)
        .then(async (result) => {
          log.collapse(
            result,
            "Successfully created Klaviyo profile using createKlaviyoProfile",
            "success"
          );
          if (result.status === 404 && reqSequence < 3) {
            QuizResults.createKlaviyoProfile(answers, email, reqSequence + 1);
          }
        })
        .catch((error) => {
          log.collapse(
            error,
            "Error creating Klaviyo profile with createKlaviyoProfile. Retrying...",
            "error"
          );
          reqSequence < 3 &&
            QuizResults.createKlaviyoProfile(answers, email, reqSequence + 1);
        });
    } else {
      reqSequence < 3 &&
        QuizResults.createKlaviyoProfile(answers, email, reqSequence + 1);
    }
  },

  trackQuizCompleted(answers) {
    if (!answers || !answers.email) return;

    // Track quiz completed event in Klaviyo
    KLAVIYO_API.trackQuizCompleted(answers)
      .then((result) => {
        result
          ? log.collapse(
              "Successfully tracked quiz completion event",
              "Quiz Completion Event",
              "success"
            )
          : log.collapse(
              "Failed to track quiz completion event",
              "Quiz Completion Event",
              "error"
            );
      })
      .catch((error) =>
        log.collapse(error, "Error tracking quiz completion event:", "error")
      );
  },
};

// ========================= Quiz Offer Module =========================
const QuizOffer = {
  selectedVariants: {
    trial: null,
    monthly: null,
  },
  _isAddingToCart: false,

  init() {
    log = window.log;
    const quizAnswers = QuizStorage.getQuizAnswers();

    // Redirect to quiz start if no answers or incomplete quiz
    if (
      !DEBUGGING &&
      !IS_SHOPIFY_EDITOR &&
      (!quizAnswers || !quizAnswers.isCompleted)
    ) {
      log.collapse(
        "No completed quiz answers found",
        "Quiz Incomplete",
        "error"
      );
      window.location.href = "/";
      return;
    }

    // Store expanded dog details
    QuizStorage.setDogDetails(quizAnswers);

    // Initialize product variants first
    this.initializeProductVariants(quizAnswers);

    // Initialize UI components
    this.bindOfferEvents();
    this.replaceDynamicContent(quizAnswers);
    this.setupProductDisplay();

    // Track offer viewed event if email is available
    if (KLAVIYO_ENABLED && quizAnswers.email) {
      const dogDetails = QuizStorage.getDogDetails();
      const productVariant = QuizStorage.getProductVariant();
      KLAVIYO_API.trackOfferViewed(
        quizAnswers.email,
        dogDetails,
        productVariant
      );
    }

    QuizStorage.clearExpiredData();
  },

  initializeProductVariants(answers) {
    try {
      if (!window.ProductConfig) {
        log.collapse(
          "ProductConfig is not defined",
          "ProductConfig Not Defined",
          "error"
        );
        return null;
      }
      log.collapse(answers, "Initializing product variants with answers");

      // Use the new getProductVariants function to get both trial and monthly variants
      const variants = window.ProductConfig.getProductVariants(answers);

      if (!variants || !variants.monthly || !variants.trial) {
        log.collapse(
          "Failed to get product variants",
          "Product Variants Not Found",
          "error"
        );
      }

      // Store the variants
      this.selectedVariants.monthly = variants.monthly;
      this.selectedVariants.trial = variants.trial;

      // Log the selected variants
      log.collapse(
        {
          trial: {
            title: this.selectedVariants.trial.title,
            weight: this.selectedVariants.trial.weight,
            variantId: this.selectedVariants.trial.variantId,
            isGrainFree: this.selectedVariants.trial.isGrainFree,
          },
          monthly: {
            title: this.selectedVariants.monthly.title,
            weight: this.selectedVariants.monthly.weight,
            variantId: this.selectedVariants.monthly.variantId,
            isGrainFree: this.selectedVariants.monthly.isGrainFree,
          },
        },
        "Selected product variants"
      );

      // Store both variants in QuizStorage
      // Store monthly variant details with expiry
      QuizStorage.setProductVariant(
        this.selectedVariants.monthly,
        this.selectedVariants.monthly.isGrainFree
      );

      // Store trial variant details with expiry using the new method
      QuizStorage.setTrialVariant(
        this.selectedVariants.trial,
        this.selectedVariants.trial.isGrainFree
      );

      // Update UI with the product variants
      this.setupProductDisplay();

      return this.selectedVariants;
    } catch (error) {
      log.collapse(error, "Error initializing product variants:", "error");
      return null;
    }
  },

  setupProductDisplay() {
    const { trial, monthly } = this.selectedVariants;
    if (!trial || !monthly) {
      log.collapse(
        "Products not found to display",
        "Products Not Found",
        "error"
      );
      return;
    }

    // Log the products being displayed
    log.collapse(
      {
        trial: {
          title: trial.title,
          weight: trial.weight,
          price: trial.price,
          variantId: trial.variantId,
        },
        monthly: {
          title: monthly.title,
          weight: monthly.weight,
          price: monthly.price,
          variantId: monthly.variantId,
        },
      },
      "Setting up product display"
    );

    try {
      const currencyFormat = $(".qz_main_offer").data("currency") || "€0";

      const formatMoney = (amount) => {
        const currency = currencyFormat.replace("0,00", "").replace("0", "");
        return currency + amount.toFixed(2);
      };

      // Update tab prices with validation
      const trialPrice = parseFloat(trial.price);
      const monthlyPrice = parseFloat(monthly.price);

      if (!isNaN(trialPrice)) {
        $('.qz_mo_plan_title[data-tab="trial"] .tab-price').text(
          formatMoney(trialPrice)
        );
      }
      if (!isNaN(monthlyPrice)) {
        $('.qz_mo_plan_title[data-tab="monthly"] .tab-price').text(
          formatMoney(monthlyPrice)
        );
      }

      // Show/hide shipping based on active tab's price
      const activeTab = $(".qz_mo_plan_title.active").data("tab");
      const activePrice = activeTab === "trial" ? trialPrice : monthlyPrice;
      if (isNaN(activePrice)) return;

      const $shippingInfo = $(".shipping_info");
      if (activePrice >= FREE_SHIPPING_THRESHOLD) {
        $shippingInfo.show();
      } else {
        $shippingInfo.hide();
      }

      $(".total_price").text(formatMoney(activePrice));

      // Show appropriate product type content
      if (monthly.isGrainFree) {
        $(".grain_free_content").show();
        $(".low_grain_content").hide();
      } else {
        $(".grain_free_content").hide();
        $(".low_grain_content").show();
      }

      // Bind tab change event for price updates
      $(".qz_mo_plan_title")
        .off("click.priceUpdate")
        .on("click.priceUpdate", function () {
          const tabId = $(this).data("tab");
          const product = tabId === "trial" ? trial : monthly;

          // Update price display
          if (product && !isNaN(parseFloat(product.price))) {
            $(".total_price").text(formatMoney(parseFloat(product.price)));

            // Update shipping info
            if (parseFloat(product.price) >= FREE_SHIPPING_THRESHOLD) {
              $shippingInfo.show();
            } else {
              $shippingInfo.hide();
            }
          }
        });
    } catch (error) {
      log.collapse(error, "Error setting up product display:", "error");
    }
  },

  bindOfferEvents() {
    // Plan type selection (trial vs monthly)
    $(".qz_mo_plan_title").click(function () {
      const planType = $(this).data("tab");
      const quizAnswers = QuizStorage.getQuizAnswers();

      // Track plan selection if email is available
      if (KLAVIYO_ENABLED && quizAnswers && quizAnswers.email) {
        const productData =
          planType === "trial"
            ? QuizStorage.getTrialVariant()
            : QuizStorage.getProductVariant();

        KLAVIYO_API.trackOfferPlanClicked(
          quizAnswers.email,
          planType,
          productData
        ).catch((error) => {
          log.collapse(
            error,
            `Error tracking ${planType} plan click:`,
            "error"
          );
        });
      }
    });

    // Add to cart button
    $(".Offer_product_atc_button").click(function () {
      const currentTitle = $(this).attr("ga_data");
      log.collapse(currentTitle, "Add to Cart Button Clicked");

      // Get selected products and add to cart
      const selectedProducts = QuizOffer.getSelectedProducts();

      log.collapse(selectedProducts, "Selected products to add to cart");
      QuizOffer.addToCart(selectedProducts);
    });

    // Close cart button
    $(".Thrive_Cart_close_buton").click(function () {
      // Enable redirect for exit intent
      const wait_url = $(".wait_page_url").attr("data") || "/pages/wait";
      window.location = wait_url;
    });

    // Show cart close button when thrivecart button is clicked
    $(document).on("click", ".thrivecart-button", function () {
      $(".Thrive_Cart_close_buton").show();
    });

    // Scroll to offer section buttons
    $(".Offer_main_includes_form_button, .Main_offer_buttons").click(
      function () {
        Utils.scrollToSection(".Offer__section__js", -50);
      }
    );
  },

  replaceDynamicContent(data) {
    if (!data) return;

    // Add trial weight to the data for dynamic content replacement
    if (this.selectedVariants && this.selectedVariants.trial) {
      // Extract the weight from the trial variant
      let trialWeight = this.selectedVariants.trial.weight || "3kg";

      // Extract only the numeric part if it's a string
      if (typeof trialWeight === "string") {
        const match = trialWeight.match(/[\d.]+/);
        if (match) {
          // Keep only the numeric part for display
          trialWeight = match[0];
        }
      }

      // Add it to the data object for dynamic content replacement
      data.trial_weight = trialWeight;
      log.collapse(trialWeight, "Added trial weight to dynamic content data");
    }

    // Use the global replaceDynamicContent function
    replaceDynamicContent(data);
  },

  getSelectedProducts() {
    // Get selected plan type
    const selectedPlan = $(".qz_mo_plan_title.active").data("tab");
    log.collapse(selectedPlan, "Selected plan type");

    let storedVariant;

    // Get the appropriate variant based on the selected plan
    if (selectedPlan === "trial") {
      storedVariant = QuizStorage.getTrialVariant();
      log.collapse(storedVariant, "Using trial variant");
    } else {
      storedVariant = QuizStorage.getProductVariant();
      log.collapse(storedVariant, "Using monthly variant");
    }

    if (!storedVariant) {
      log.collapse(
        `No ${selectedPlan} variant found`,
        "No Variant Found",
        "error"
      );
      return [];
    }

    // Combine products with proper quantities
    return [
      {
        variantId: storedVariant.variantId,
        quantity: 1,
        planType: selectedPlan,
        isGrainFree: storedVariant.options?.age !== undefined, // If age option exists, it's grain-free
      },
    ];
  },

  addToCart(products) {
    // Add safeguard to prevent multiple calls
    if (this._isAddingToCart) {
      log.collapse(
        "Add to cart already in progress",
        "Add to Cart In Progress",
        "warning"
      );
      return;
    }

    this._isAddingToCart = true;
    log.collapse(products, "Adding to cart");

    // Get stored details
    const dogDetails = QuizStorage.getDogDetails();
    const quizAnswers = QuizStorage.getQuizAnswers();

    if (!dogDetails || !quizAnswers) {
      log.collapse(
        "Missing required data for cart",
        "Missing Required Data",
        "error"
      );
      return;
    }

    // Get the first product (we only support one product at a time)
    const product = products[0];
    if (!product) {
      log.collapse("No product to add to cart", "No Product to Add", "error");
      return;
    }

    // Get the selected plan type
    const selectedPlan = product.planType || "monthly";

    // Get the appropriate variant based on the selected plan
    let variant;
    if (selectedPlan === "trial") {
      variant = this.selectedVariants.trial;
      log.collapse(variant, "Using trial variant for cart");
    } else {
      variant = this.selectedVariants.monthly;
      log.collapse(variant, "Using monthly variant for cart");
    }

    if (!variant) {
      log.collapse(
        `No ${selectedPlan} variant found for cart`,
        "No Variant Found",
        "error"
      );
      return;
    }

    // Get selling plan if available
    const sellingPlan =
      variant.rechargePlans && variant.rechargePlans.length > 0
        ? variant.rechargePlans[0]
        : null;

    // Get frequency from selling plan
    const frequency =
      sellingPlan?.subscription_preferences?.order_interval_frequency || 4;

    // Generate a UUID for the form
    const formId = uuid();

    try {
      const formAttributes = [{ key: "formId", value: "pt-" + formId }];
      if (selectedPlan === "trial") {
        const { variantId, productId, price } = this.selectedVariants.monthly;
        formAttributes.push(
          { key: "order_trial", value: "true" },
          { key: "external_variant_id", value: variantId },
          { key: "external_product_id", value: productId },
          { key: "subscription_price", value: price },
          { key: "dog_weight", value: dogDetails.weight }
        );
      } else {
        formAttributes.push(
          { key: "order_trial", value: null },
          { key: "external_variant_id", value: null },
          { key: "external_product_id", value: null },
          { key: "subscription_price", value: null },
          { key: "dog_weight", value: null }
        );
      }

      // Store email in localStorage for checkout domain
      localStorage.setItem("checkout_email", quizAnswers.email);

      const dogObject = {
        name: dogDetails.name,
        gender: dogDetails.gender,
        weight: dogDetails.weight,
        breed: dogDetails.breed || "",
      };
      if (dogDetails.age) dogObject.lifeStage = dogDetails.age;

      const dogObjectKlaviyo =
        KLAVIYO_API.quizAnswersToProfileProperties(quizAnswers);

      const lineItemAttributes = [
        { key: "_dogs", value: JSON.stringify([dogObject]) },
        { key: "_dogs_klaviyo", value: JSON.stringify([dogObjectKlaviyo]) },
        { key: "_subscriptionType", value: "monthlySubscription_v1" },
      ];

      // Build cart items with properties and selling plan
      const items = [
        {
          id: variant.variantId,
          quantity: 1,
          selling_plan: sellingPlan?.external_plan_id, // Add selling plan ID if available
          properties: Utils.formatArrayToObject(lineItemAttributes),
        },
      ];

      // Track add to cart event in Klaviyo if email is available
      if (KLAVIYO_ENABLED && quizAnswers.email) {
        KLAVIYO_API.trackOfferAddedToCart(
          quizAnswers.email,
          variant,
          selectedPlan
        ).catch((error) => {
          log.collapse(error, "Error tracking add to cart event:", "error");
        });
      }

      // First clear the cart
      $.ajax({
        type: "POST",
        url: "/cart/clear.js",
        dataType: "json",
      })
        .then(() => {
          // Then add new items with order attributes
          return $.ajax({
            type: "POST",
            url: "/cart/add.js",
            data: {
              items,
              attributes: Utils.formatArrayToObject(formAttributes),
            },
          });
        })
        .then(() => {
          window.location.href = "/checkout";
        })
        .catch((error) => {
          log.collapse(error, "Error managing cart:", "error");
          alert("There was an error updating your cart. Please try again.");
          this._isAddingToCart = false;
        });
    } catch (error) {
      log.collapse(error, "Error preparing cart items:", "error");
      alert("There was an error preparing your items. Please try again.");
      this._isAddingToCart = false;
    }
  },

  getWeightRange(weight) {
    // Convert weight to a range category (small, medium, large)
    const weightNum = parseFloat(weight);

    if (weightNum < 10) {
      return "small";
    } else if (weightNum < 25) {
      return "medium";
    } else {
      return "large";
    }
  },
};

// ========================= Loading Animations =========================
const LoadingAnimations = {
  runEmailChecker() {
    // Calculating Percentage function
    const Calculating = () => {
      let data = parseFloat($(".Quiz_calculating_percent__js").attr("data"));
      if (data < 100) {
        // Progressive loading animation
        if (data < 20) data++;
        else if (data >= 20 && data < 22) data += 0.15;
        else if (data >= 22 && data < 29) data += 1.3;
        else if (data >= 29 && data < 32) data += 3;
        else if (data >= 32 && data < 45) data += 4;
        else if (data >= 45 && data < 48) data += 0.7;
        else if (data >= 48 && data < 57) data += 1.4;
        else if (data >= 57 && data < 87) data += 4;
        else if (data >= 87 && data < 93) data += 2.3;
        else if (data >= 93 && data < 97) data += 4;
        else if (data >= 97 && data < 100) data += 0.4;

        if (data >= 100) {
          const next_url = $(".Quiz_calculating_percent__js").attr("next_url");
          window.location.replace(next_url);
        }
      }

      const data_rounded = Math.round(data);
      $(".Quiz_calculating_percent__js").attr("data", data);
      $(".Quiz_calculating_percent__js").text(data_rounded + "%");
    };

    // Setup loading UI
    $(".Quiz_question").hide();
    $(".Quiz_loading_page").css("display", "flex");
    $(".Quiz-header_progres_bar__inner").css("width", "100%");
    $(".Quiz_header_back").css("opacity", "0").css("cursor", "default");

    setTimeout(() => {
      $(".Quiz_question").hide();
    }, 100);

    // Start calculation animation
    setInterval(Calculating, 100);
  },

  runMiddleCalculation() {
    const current_index = parseFloat(
      $(".Quiz_middle_loading__js").attr("data-index")
    );
    const next_index = current_index + 1;

    // Setup loading UI
    $(".Quiz_question").hide();
    $(".Quiz_middle_loading").css("display", "flex");

    const MiddleCalculating = () => {
      let data = parseFloat($(".Quiz_middle_loading__js").attr("data"));
      if (data < 100) {
        // Progressive loading animation
        if (data < 20) data++;
        else if (data >= 20 && data < 22) data += 0.15;
        else if (data >= 22 && data < 29) data += 1.3;
        else if (data >= 29 && data < 32) data += 3;
        else if (data >= 32 && data < 45) data += 4;
        else if (data >= 45 && data < 48) data += 0.7;
        else if (data >= 48 && data < 57) data += 0.4;
        else if (data >= 57 && data < 87) data += 3;
        else if (data >= 87 && data < 93) data += 2;
        else if (data >= 93 && data < 97) data += 4;
        else if (data >= 97 && data < 100) data += 0.4;

        if (data >= 100) {
          // Passing to the next part of Quiz
          QuizNavigation.moveToNextQuestion(current_index);
          clearInterval(calculationInterval);
          $(".Quiz_middle_loading").hide();
        }
      }

      const data_rounded = Math.round(data);
      $(".Quiz_middle_loading__js").attr("data", data);
      $(".Quiz_middle_loading__js").text(data_rounded + "%");
    };

    // Start calculation animation
    const calculationInterval = setInterval(MiddleCalculating, 100);
  },
};

// ========================= Quiz Start Module =========================
const QuizStart = {
  init() {
    log = window.log;
    this.bindStartButton();

    // Track quiz start viewed event
    // Note: We don't have email at this point, so we can't track it with a profile
    // But we can implement this later when we have email capture on the start page
    log.collapse("Quiz start page viewed", "Quiz start page viewed", "info");
  },

  bindStartButton() {
    $(".quiz_start_button").click(function () {
      // Track quiz started event if possible
      // For now, we just log it
      log.collapse(
        "Quiz started button clicked",
        "Quiz started button clicked",
        "info"
      );

      window.location.href = "/pages/quiz-main";
    });
  },
};

// ========================= Initialization =========================
$(document).ready(function () {
  // Check if ProductConfig is available
  if (typeof window.ProductConfig === "undefined") {
    log.collapse(
      "ProductConfig is not defined. Make sure quiz_product_config.js is loaded before quiz_main.js",
      "ProductConfig Not Defined",
      "error"
    );
  }

  // Initialize page-specific modules based on current page
  const currentPath = window.location.pathname;

  if (currentPath.includes("/pages/quiz-start")) {
    // TODO: Send QUIZ_START_VIEWED event to Klaviyo
    QuizStart.init();
  } else if (currentPath.includes("/pages/quiz-main")) {
    QuizMain.init();
  } else if (currentPath.includes("/pages/quiz-results")) {
    // TODO: Send RESULTS_VIEWED event to Klaviyo
    QuizResults.init();
  } else if (currentPath.includes("/pages/longevity-plan-quiz-results")) {
    // TODO: Send OFFER_VIEWED event to Klaviyo
    QuizOffer.init();
  }

  // Initialize dynamic content replacement on any page
  initializeDynamicContent();
});

// Make the middle calculation function available globally for onclick handlers
function Start_middle_calculating() {
  LoadingAnimations.runMiddleCalculation();
}

// Make the email checker function available globally for onclick handlers
function runEmailChecker() {
  LoadingAnimations.runEmailChecker();
}

function replaceDynamicContent(data, context = "all") {
  if (!data) return;

  // Calculate trial weight if not already included
  if (!data.trial_weight && data.weight) {
    const weightNum = parseFloat(data.weight.toString().replace(/kg/i, ""));
    data.trial_weight = weightNum <= 6 ? "1kg" : "3kg";
    log.collapse(
      data.trial_weight,
      "Calculated trial weight for dynamic content"
    );
  }

  // Replace content in dynamic elements
  const replaceContent = (element) => {
    const dataValue = element.getAttribute("data-value");
    if (!dataValue) return;

    let newValue = null;
    if (typeof data === "object") {
      // Handle special case for gender_prefix
      if (dataValue === "gender_prefix") {
        const gender = data.gender || (data.dog && data.dog.gender);
        newValue = gender && gender.toLowerCase() === "macho" ? "do" : "da";
      } else if (dataValue === "weight") {
        // For weight, use foodBagWeight from quiz answers
        newValue = data.foodBagWeight;
      } else if (data[dataValue]) {
        newValue = data[dataValue];
      } else if (data.dog && data.dog[dataValue]) {
        newValue = data.dog[dataValue];
      } else if (data.owner && data.owner[dataValue]) {
        newValue = data.owner[dataValue];
      }

      // Special handling for weight and trial_weight
      if (
        (dataValue === "weight" || dataValue === "trial_weight") &&
        newValue
      ) {
        // Remove 'kg' suffix and any spaces
        newValue = newValue
          .toString()
          .trim()
          .replace(/\s+/g, "")
          .replace(/kg/i, "");
      }
    }

    if (newValue) {
      element.innerHTML = newValue;
    }
  };

  // Process all dynamic elements
  document.querySelectorAll(".dynamic_content_js").forEach(replaceContent);

  // Handle conditional content
  const weightCategory =
    data.weightCategory || (data.dog && data.dog.weightCategory);
  if (weightCategory) {
    // $(`.qz_results_in_padding[data-type="weight"]`).hide();
    // $(`.qz_results_in_padding[data-type="weight"][data-handle="${weightCategory}"]`).show();
  }

  const typeOfFood = data.typeOfFood || (data.dog && data.dog.typeOfFood);
  if (typeOfFood) {
    $(`.qz_results_in_padding[data-type="diet"]`).hide();
    $(
      `.qz_results_in_padding[data-type="diet"][data-handle="${typeOfFood}"]`
    ).show();
  }

  // Handle age-specific content
  const age = data.age || (data.dog && data.dog.age);
  if (age) {
    const ageCategory = window.ProductConfig
      ? window.ProductConfig.mapAgeCategory(age)
      : age.toLowerCase();

    $(`.age_specific_content[data-age="${ageCategory}"]`).show();
  }

  // Handle weight-specific content
  const weight = data.weight || (data.dog && data.dog.weight);
  if (weight && window.ProductConfig) {
    const weightRange = window.ProductConfig.getWeightRange(weight);
    $(`.weight_specific_content[data-weight-range="${weightRange}"]`).show();
  }
}

// Function to initialize dynamic content replacement on any page
function initializeDynamicContent() {
  const quizAnswers = QuizStorage.getQuizAnswers();
  const profile = QuizProfile.getProfile();

  // Merge quiz answers and profile data
  const combinedData = { ...quizAnswers, ...profile };

  // Replace dynamic content
  replaceDynamicContent(combinedData);
}

// Add QuizProfile module to store and manage owner and dog information
window.QuizProfile = (function () {
  // Default profile structure
  const defaultProfile = {
    owner: {
      name: "",
      email: "",
      phone: "",
    },
    dog: {
      name: "",
      breed: "",
      age: "",
      weight: "",
      concerns: "",
      healthConcerns: [],
    },
  };

  // Get profile from localStorage or create a new one
  function getProfile() {
    const storedProfile = LocalStorageManager.getWithExpiry("quizProfile");
    return storedProfile ? storedProfile : { ...defaultProfile };
  }

  // Save profile to localStorage with 14-day expiry
  function saveProfile(profile) {
    LocalStorageManager.setWithExpiry("quizProfile", profile, 14);
  }

  // Update specific profile field
  function updateProfile(section, field, value) {
    const profile = getProfile();

    if (profile[section] && field) {
      profile[section][field] = value;
      saveProfile(profile);
    }
    return profile;
  }

  // Update multiple profile fields at once
  function updateProfileBatch(updates) {
    const profile = getProfile();

    Object.entries(updates).forEach(([section, fields]) => {
      if (profile[section]) {
        Object.entries(fields).forEach(([field, value]) => {
          profile[section][field] = value;
        });
      }
    });

    saveProfile(profile);
    return profile;
  }

  // Clear profile data
  function clearProfile() {
    LocalStorageManager.removeItem("quizProfile");
    return { ...defaultProfile };
  }

  // Return public API
  return {
    getProfile,
    updateProfile,
    updateProfileBatch,
    clearProfile,
  };
})();

// ========================= Storage Management =========================
const LocalStorageManager = {
  setWithExpiry(key, value, ttl) {
    const item = {
      value: value,
      expiry: new Date().getTime() + ttl * 24 * 60 * 60 * 1000, // Convert days to milliseconds
    };
    localStorage.setItem(key, JSON.stringify(item));
  },

  getWithExpiry(key) {
    const itemStr = localStorage.getItem(key);
    if (!itemStr) return null;

    const item = JSON.parse(itemStr);
    const now = new Date().getTime();

    if (now > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return item.value;
  },

  removeItem(key) {
    localStorage.removeItem(key);
  },
};

const QuizNavigation = {
  /**
   * Moves to the next question in the quiz
   * @param {number} currentIndex - The current question index
   * @returns {boolean} - Whether the navigation was to the results page
   */
  async moveToNextQuestion(currentIndex) {
    const nextIndex = currentIndex + 1;
    const nextQuestionIndex = parseFloat(
      $(`.Quiz_question_${currentIndex}__js`).attr("data-question-index")
    );

    const $nextQuestionBlock = $(`.Quiz_question_${nextIndex}__js`);

    if ($nextQuestionBlock.attr("data-is-protein") === "true") {
      const answers = QuizMain.getAnswers();

      const ageKey = (answers.age || "").toLowerCase();
      const puppyKey = ["puppy"];
      const isPuppy = puppyKey.includes(ageKey);

      const $venisonOption = $nextQuestionBlock.find('[data-key="venison"]');
      const $hypoOption = $nextQuestionBlock.find(
        '[data-key="hypoallergenic"]'
      );

      if (isPuppy) {
        $venisonOption.hide();
        $hypoOption.hide();

        $venisonOption.removeClass("active");
        $hypoOption.removeClass("active");
      } else {
        $venisonOption.show();
        $hypoOption.show();
      }
    }

    // Hide current question and show next
    $(".Quiz_question").hide();
    $(`.Quiz_question_${nextIndex}__js`).css("display", "flex");

    // Calculate progress bar width
    const questionLength = parseFloat($(".Question_amount").val()) + 1;
    const onlyQuestions = parseFloat(
      $(".Question_amount").attr("data-questions_amount")
    );
    const multiplier = 100 / onlyQuestions;
    const width = nextQuestionIndex * multiplier;

    // Check if we've reached the end of the quiz
    if (questionLength == nextIndex) {
      // Get the answers and mark them as completed
      const answers = QuizMain.getAnswers();
      // Save answers with completed flag
      QuizStorage.setQuizAnswers(answers, true);

      log("CREATING KLAVIYO PROFILE");
      const email = answers.email;

      if (email) {
        await postDigitalOceanKlaviyoQuiz(answers);
      }

      window.location.href = "/pages/longevity-plan-quiz-results";
      return true;
    } else {
      // Update question counter and back button
      $(".Quiz_header_question_info_js").text(
        nextQuestionIndex + "/" + onlyQuestions
      );
      $(".Quiz_header_back")
        .css("opacity", "1")
        .attr("data-current", nextIndex)
        .css("cursor", "pointer");

      QuizMain.calculateTop();

      // Refresh carousel if it exists
      if ($(".qz_reviews_carousel").length) {
        $(".qz_reviews_carousel").slick("refresh");
      }

      return false;
    }
  },
};
